import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { CHAINS } from '../constants'
import type { ProfileData, ConnectionMethod } from '../hooks/useWallet'

export type AppView = 'hub' | 'marketplace' | 'add-template' | 'skill'

type WalletSummary = {
  isConnected: boolean
  isConnecting: boolean
  address: string | null
  chainId: number | null
  profileData: ProfileData | null
  connectionMethod: ConnectionMethod
  isExtensionAvailable: boolean
  isWalletConnectAvailable: boolean
  connectExtension: () => void
  connectWalletConnect: () => void
  disconnect: () => void
}

export function AppShell({
  view,
  onViewChange,
  wallet,
  children,
}: {
  view: AppView
  onViewChange: (view: AppView) => void
  wallet: WalletSummary
  children: ReactNode
}) {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)

  useEffect(() => {
    setIsSidebarCollapsed(localStorage.getItem('sidebarCollapsed') === 'true')
  }, [])

  const toggleSidebar = () => {
    const next = !isSidebarCollapsed
    setIsSidebarCollapsed(next)
    localStorage.setItem('sidebarCollapsed', String(next))
  }

  const chain = useMemo(() => {
    const chainId = wallet.chainId ?? 42
    const found = Object.values(CHAINS).find(c => c.id === chainId)
    return found || CHAINS.lukso
  }, [wallet.chainId])

  return (
    <div className="min-h-screen bg-sidebar text-gray-100">
      <div className="flex flex-col md:flex-row md:min-h-screen md:h-screen md:overflow-hidden">
        {/* Sidebar — fixed on desktop; only main content scrolls */}
        <aside
          className={`${isSidebarCollapsed ? 'md:w-[72px]' : 'md:w-60'} md:flex-shrink-0 md:h-screen md:min-h-0 transition-all duration-200 flex flex-col bg-sidebar`}
        >
          <div className="flex h-full flex-col gap-1 px-2 sm:px-4 md:px-3 pt-4 md:pt-5 pb-6 md:pb-4">
            <div className="flex items-center justify-between md:block px-2">
              <div className={`flex items-center px-1 gap-3 ${isSidebarCollapsed ? 'justify-center w-full' : ''}`}>
                <svg className="w-4 h-4 text-gray-300 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <rect x="3" y="3" width="7" height="7" rx="1.5" />
                  <rect x="14" y="3" width="7" height="7" rx="1.5" />
                  <rect x="3" y="14" width="7" height="7" rx="1.5" />
                  <rect x="14" y="14" width="7" height="7" rx="1.5" />
                </svg>
                {!isSidebarCollapsed && (
                  <div className="min-w-0">
                    <div className="text-sm font-semibold tracking-tight text-white truncate">
                      Permission Control
                    </div>
                  </div>
                )}
              </div>
            </div>

            <nav className="mt-4 md:mt-6 flex flex-row md:flex-col gap-0.5 overflow-x-auto md:overflow-visible pb-1 md:pb-0">
              <SideNavItem label="Hub" icon="hub" collapsed={isSidebarCollapsed} active={view === 'hub'} onClick={() => onViewChange('hub')} />
              <SideNavItem
                label="Templates"
                icon="marketplace"
                collapsed={isSidebarCollapsed}
                active={view === 'marketplace' || view === 'add-template'}
                onClick={() => onViewChange('marketplace')}
              />
            </nav>

            <div className={`hidden md:flex mt-auto gap-2 ${isSidebarCollapsed ? 'flex-col items-center' : 'items-center justify-between w-full'}`}>
              {!isSidebarCollapsed && (
                <div className="flex items-center gap-2 min-w-0 px-2 pl-4">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse flex-shrink-0" />
                  <span className="text-sm text-gray-400 truncate">{chain.name}</span>
                </div>
              )}
              <button
                onClick={toggleSidebar}
                className="flex items-center justify-center rounded-lg text-gray-500 hover:bg-white/5 transition-all duration-200 py-2.5 px-2"
                aria-label={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <span className="flex items-center justify-center shrink-0">
                  {isSidebarCollapsed ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  )}
                </span>
              </button>
            </div>
          </div>
        </aside>

        {/* Main — elevated panel with sharp light-gradient edge + shadow */}
        <main className="mt-0 flex-1 min-w-0 flex flex-col px-3 md:min-h-0 md:p-3 md:pb-4">
          <div
            className="panel-sharp flex-1 flex flex-col min-h-0 rounded-xl overflow-hidden bg-surface md:min-h-[calc(100vh-1.5rem)]"
          >
            <div className="flex-1 py-6 md:py-8 overflow-auto scrollbar-minimal">
              <div className="mx-auto max-w-3xl px-4 sm:px-6">
                {children}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

function SideNavItem({
  label,
  icon,
  collapsed,
  active,
  onClick,
}: {
  label: string
  icon: 'hub' | 'marketplace'
  collapsed: boolean
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`
        relative flex items-center px-3 py-2.5 rounded-lg text-sm transition-all duration-200 whitespace-nowrap md:w-full
        nav-item-sharp-hover
        ${active ? 'nav-item-active' : ''}
        ${collapsed ? 'md:justify-center md:px-2' : 'text-left'}
        ${active
          ? 'bg-[#232323] text-white'
          : 'text-gray-400 hover:bg-white/5'
        }
      `}
      title={collapsed ? label : undefined}
    >
      <span className={`inline-flex items-center gap-3 ${collapsed ? 'md:gap-0' : ''}`}>
        <span className={`flex items-center justify-center shrink-0 ${active ? 'text-gray-200' : 'text-gray-500'}`}>
          <NavItemIcon kind={icon} />
        </span>
        {!collapsed && <span className="font-medium">{label}</span>}
      </span>
    </button>
  )
}

function NavItemIcon({ kind }: { kind: 'hub' | 'marketplace' }) {
  if (kind === 'hub') {
    return (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10.5L12 3l9 7.5M5 9.5V21h14V9.5" />
      </svg>
    )
  }

  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10l1.5-5h15L21 10M4 10h16M6 10v9h12v-9M9 19v-4h6v4" />
    </svg>
  )
}

